import { type DependencyList, useLayoutEffect, useRef } from 'react';

import { highlightElement } from './highlight';

/**
 * Highlights the referenced `<code>` element whenever `deps` change, and returns the ref to
 * attach to it.
 *
 * Pass whatever identifies the rendered snippet — typically its content and language.
 *
 *   const codeEl = useHighlightedCode([children, lang]);
 *   <code className={`language-${lang}`} ref={codeEl}>{children}</code>
 */
export function useHighlightedCode(deps: DependencyList) {
  const codeEl = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const element = codeEl.current;
    if (!element) {
      return;
    }

    // Use requestAnimationFrame to ensure that the element is mounted
    // before highlighting it.
    const frame = requestAnimationFrame(() => {
      highlightElement(element);
    });

    // Cancel the animation frame when the component unmounts.
    return () => cancelAnimationFrame(frame);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- deps are supplied by the caller
  }, deps);

  return codeEl;
}
